import { NotificationType } from 'common/enums';
import { VideoCall } from 'common/video-call';
import { VideoCallEnded } from 'common/video-call-ended';
import { appActions, AppContext } from 'context';
import { hasVaultAccess, isOwner } from 'helpers/utils';
import { VideoCallRole } from 'hooks/use-videocall-websocket';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { goToPage, RouteName } from 'routes';

type ParamType = {
  agoraVaultId: string;
  videoCallToken: string;
  role: VideoCallRole;
};

const AgoraVideoCall: React.FC = () => {
  const { state, dispatch } = useContext(AppContext);

  const { agoraVaultId, videoCallToken, role } = useParams<ParamType>();
  // Started from an album's planner: the interview asks its prompts only.
  const albumId =
    Number(new URLSearchParams(useLocation().search).get('album')) || undefined;

  const [vault, setVault] = useState<VaultType>();
  const [showVideocallEnded, setShowVideocallEnded] = useState(false);
  const [isGuest, setIsGuest] = useState(false);

  const { user } = state.data;

  const onEndCall = async () => {
    try {
      if (vault && !hasVaultAccess(user, vault)) {
        goToPage(RouteName.SignIn);
      } else {
        goToPage(RouteName.VaultDetail, { id: agoraVaultId });
      }
    } catch (_error) {
      goToPage(RouteName.SignIn);
    }
  };

  const handleEndCall = async (limitReached?: boolean) => {
    if (limitReached) {
      setShowVideocallEnded(true);
    } else {
      await onEndCall();
    }
  };

  const notifyEndCallGuest = () => {
    setShowVideocallEnded(true);
    setIsGuest(true);
  };

  const getVault = async () => {
    try {
      const vaultResponse = await VaultsController.getVideoCallVault(
        +agoraVaultId,
        videoCallToken,
      );
      setVault(vaultResponse);
    } catch (err: any) {
      await onEndCall();

      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Video call',
          message: 'Vault Information fails',
          type: NotificationType.Error,
        }),
      });
    }
  };

  useEffect(() => {
    getVault();
  }, []);

  const showVideoCallComponent = () => (
    <div>
      <VideoCall
        id={agoraVaultId}
        user={user}
        vault={vault!}
        subSchemes={state.subscriptionsScheme!}
        notifyVaultChange={(alb: VaultType) => setVault(alb)}
        notifyEndCall={handleEndCall}
        refetchVault={getVault}
        initialRole={role}
        albumId={albumId}
        notifyEndCallGuest={notifyEndCallGuest}
      />
    </div>
  );

  if (showVideocallEnded)
    return (
      <VideoCallEnded
        notifyClose={() => {
          setShowVideocallEnded(false);
          onEndCall();
        }}
        isGuest={isGuest}
      />
    );
  return vault && (!isOwner(user, vault) || state.subscriptionsScheme?.length)
    ? showVideoCallComponent()
    : null;
};

export { AgoraVideoCall };
